import { Theme } from "react-native-elements";
import Color from "tinycolor2";
import gradient from "tinygradient";

export const LEFT_NAV_WIDTH = 160;
export const UNIT = 8;
export const MAX_CONTENT_WIDTH = UNIT * 64;

export const FONT_SIZE_PRIMARY = 14;
export const FONT_SIZE_SECONDARY = 12;

export const LIST_IMAGE_WIDTH = UNIT * 8;
export const LIST_IMAGE_HEIGHT = UNIT * 8;

export const SECTION_TOP_MARGIN = UNIT * 2;

export const fontFamilies = {
  ["Poppins Bold"]: "Poppins Bold",
  ["Poppins"]: "Poppins",
};

export const fonts = {
  header: fontFamilies["Poppins Bold"],
  smallHeader: fontFamilies["Poppins"],
  bodyText: fontFamilies["Poppins"],
};

const GREEN = new Color("#67bc25").desaturate(10);
const RED = new Color("#c4000c").desaturate(20).lighten(5);
const YELLOW = new Color("#efd000").desaturate(10);
const ORGANGE = new Color("#f68100").desaturate(10);

const NEUTRAL = new Color("#3f4043").toHexString();
const NEUTRAL_LIGHTEST = new Color({ r: 244, g: 244, b: 244 }).toHexString();
const NEUTRAL_GRADIENT = gradient([NEUTRAL, NEUTRAL_LIGHTEST]).rgb(18);

// Based on http://paletton.com/#uid=63-0I0k++lEZF-++Wra+Zg9+T9j
export const colors = {
  primary: GREEN.toHexString(),
  primary900: GREEN.clone().darken(21).toHexString(),
  primary800: GREEN.clone().darken(14).toHexString(),
  primary700: GREEN.clone().darken(7).toHexString(),
  primary600: GREEN.clone().toHexString(),
  primary500: GREEN.clone().lighten(7).toHexString(),
  primary400: GREEN.clone().lighten(14).toHexString(),
  primary300: GREEN.clone().lighten(21).toHexString(),
  primary200: GREEN.clone().lighten(28).toHexString(),
  primary100: GREEN.clone().lighten(35).toHexString(),
  primary050: GREEN.clone().lighten(42).toHexString(),

  green: GREEN.toHexString(),
  greenDark: GREEN.clone().darken(14).toHexString(),

  yellow: YELLOW.toHexString(),
  organge: ORGANGE.toHexString(),

  red: RED.toHexString(),
  red900: RED.clone().darken(21).toHexString(),
  red800: RED.clone().darken(14).toHexString(),
  red700: RED.clone().darken(7).toHexString(),
  red600: RED.clone().toHexString(),
  red500: RED.clone().lighten(9).toHexString(),
  red400: RED.clone().lighten(18).toHexString(),
  red300: RED.clone().lighten(27).toHexString(),
  red200: RED.clone().lighten(36).toHexString(),
  red100: RED.clone().lighten(45).toHexString(),
  red050: RED.clone().lighten(50).toHexString(),

  neutral900: NEUTRAL_GRADIENT[0].toHexString(),
  neutral800: NEUTRAL_GRADIENT[2].toHexString(),
  neutral700: NEUTRAL_GRADIENT[4].toHexString(),
  neutral600: NEUTRAL_GRADIENT[6].toHexString(),
  neutral500: NEUTRAL_GRADIENT[8].toHexString(),
  neutral400: NEUTRAL_GRADIENT[10].toHexString(),
  neutral300: NEUTRAL_GRADIENT[12].toHexString(),
  neutral200: NEUTRAL_GRADIENT[14].toHexString(),
  neutral100: NEUTRAL_GRADIENT[16].toHexString(),
  neutral050: NEUTRAL_GRADIENT[17].toHexString(),
};

export const DISABLED_TEXT_COLOR = colors.neutral300;

export const PROPERTY_INFO_MAX_IMAGE_HEIGHT = UNIT * 16;
export const PROPERTY_INFO_MAX_IMAGE_WIDTH = UNIT * 32;

export const theme: Partial<Theme> = {
  Card: {
    containerStyle: {
      padding: UNIT * 2,
      marginVertical: UNIT,
      marginLeft: 1, // Leave one pixel for the shadow
      marginRight: 0,
      backgroundColor: colors.neutral050,
      borderWidth: 0,
    },
  },
  Text: {
    style: {
      position: "relative",
      top: 1,
      lineHeight: 20,
      fontSize: FONT_SIZE_PRIMARY,
      fontFamily: fonts.bodyText,
    },
    h2Style: {
      position: "relative",
      top: 3,
      lineHeight: 24,
      fontSize: 20,
      fontWeight: "bold",
      fontFamily: fonts.bodyText,
    },
    h3Style: {
      position: "relative",
      top: 3,
      lineHeight: 16,
      fontSize: FONT_SIZE_PRIMARY,
      fontWeight: "bold",
      fontFamily: fonts.bodyText,
      marginBottom: UNIT,
    },
  },
  Button: {
    titleStyle: {
      fontFamily: fonts.bodyText,
      position: "relative",
      top: -1,
      fontSize: FONT_SIZE_PRIMARY,
      fontWeight: "500",
      paddingTop: 0,
      paddingBottom: 0,
    },
    buttonStyle: {
      height: UNIT * 4,
      backgroundColor: colors.primary,
    },
    disabledStyle: {
      backgroundColor: colors.neutral200,
    },
  },
  Input: {
    inputStyle: {
      fontFamily: fonts.bodyText,
      position: "relative",
      top: 3,
      lineHeight: UNIT * 2,
      minHeight: UNIT * 2,
      fontSize: 14,
    },
    labelStyle: {
      position: "relative",
      top: 3,
      lineHeight: UNIT * 2,
      fontFamily: fonts.smallHeader,
      color: colors.neutral900,
      fontSize: FONT_SIZE_PRIMARY,
      fontWeight: "bold",
    },
    errorStyle: {
      position: "relative",
      top: 1,
      margin: 0,
      marginTop: UNIT / 2,
      lineHeight: UNIT * 2 + 4,
      fontSize: FONT_SIZE_SECONDARY,
    },
    inputContainerStyle: {
      paddingTop: UNIT,
      paddingBottom: UNIT - UNIT / 2,
    },
    containerStyle: {
      paddingLeft: 0,
      paddingBottom: UNIT * 2 + UNIT / 2 - 1,
      margin: 0,
      maxWidth: 400,
    },
  },
} as const;
